import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, homeFor } from './context/AuthContext.jsx';
import Layout from './components/Layout.jsx';
import { Loader, Empty } from './components/ui.jsx';

import Home from './pages/Home.jsx';
import About from './pages/About.jsx';
import Contact from './pages/Contact.jsx';
import Leaderboard from './pages/Leaderboard.jsx';
import CyberPath from './pages/CyberPath.jsx';
import Login from './pages/Login.jsx';
import Checkout from './pages/Checkout.jsx';
import Account from './pages/Account.jsx';
import PcComponentsLab from './pages/labs/PcComponentsLab.jsx';
import ProgramLaunchLab from './pages/labs/ProgramLaunchLab.jsx';
import BootLoaderLab from './pages/labs/BootLoaderLab.jsx';
import OsTypesLab from './pages/labs/OsTypesLab.jsx';
import WindowsBasicsLab from './pages/labs/WindowsBasicsLab.jsx';
import WindowsCliLab from './pages/labs/WindowsCliLab.jsx';
import LinuxBasicsLab from './pages/labs/LinuxBasicsLab.jsx';
import LinuxCliLab from './pages/labs/LinuxCliLab.jsx';
import MacBasicsLab from './pages/labs/MacBasicsLab.jsx';
import MacCliLab from './pages/labs/MacCliLab.jsx';
import TradeOffsLab from './pages/labs/TradeOffsLab.jsx';
import ScopeLab from './pages/labs/ScopeLab.jsx';
import ReconLab from './pages/labs/ReconLab.jsx';
import InboxLab from './pages/labs/InboxLab.jsx';
import HardenLab from './pages/labs/HardenLab.jsx';
import WebLab from './pages/labs/WebLab.jsx';



import JuniorInternet from './pages/explainers/JuniorInternet.jsx';
import MessageJourneyLab from './pages/labs/MessageJourneyLab.jsx';
import TrapSpotterLab from './pages/labs/TrapSpotterLab.jsx';

import CourseSelect from './pages/student/CourseSelect.jsx';
import CoursePage from './pages/student/CoursePage.jsx';
import LessonPlayer from './pages/student/LessonPlayer.jsx';
import Badges from './pages/student/Badges.jsx';
import Profile from './pages/student/Profile.jsx';

import { SchoolScopeProvider } from './pages/admin/SchoolScope.jsx';
import AdminHome from './pages/admin/AdminHome.jsx';
import AdminStudents from './pages/admin/AdminStudents.jsx';
import AdminStudentDetail from './pages/admin/AdminStudentDetail.jsx';

import SuperHome from './pages/superadmin/SuperHome.jsx';
import SuperSchools from './pages/superadmin/SuperSchools.jsx';
import SuperUsers from './pages/superadmin/SuperUsers.jsx';
import SuperPayments from './pages/superadmin/SuperPayments.jsx';
import SuperAudit from './pages/superadmin/SuperAudit.jsx';
import SuperMessages from './pages/superadmin/SuperMessages.jsx';

const NAV = {
  student: [
    { to: '/learn', label: 'My courses', icon: '📖', end: true },
    { to: '/learn/badges', label: 'My badges', icon: '🏅' },
    { to: '/learn/me', label: 'My profile', icon: '🐻' },
    { to: '/cyberpath', label: 'CyberPath', icon: '🧭' },
  ],
  admin: [
    { to: '/admin', label: 'Dashboard', icon: '📊', end: true },
    { to: '/admin/students', label: 'Students', icon: '🎒' },
  ],
  superadmin: [
    { to: '/manage', label: 'Control room', icon: '🛡️', end: true },
    { to: '/manage/schools', label: 'Schools', icon: '🏫' },
    { to: '/manage/users', label: 'Users', icon: '👥' },
    { to: '/manage/payments', label: 'Payments', icon: '💳' },
    { to: '/manage/messages', label: 'Messages', icon: '📨' },
    { to: '/manage/audit', label: 'Audit log', icon: '📜' },
    { to: '/admin', label: 'School reports', icon: '📊' },
  ],
};

/** Blocks a route until the session is known, then checks the role. */
function Guard({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Loader label="Checking your pass…" />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

/** Wraps a guarded route in the shell, with the nav for the active role. */
function Shell({ roles, playful, wide, children }) {
  const { user } = useAuth();
  return (
    <Guard roles={roles}>
      <Layout nav={NAV[user?.role] ?? []} playful={playful} wide={wide}>
        {children}
      </Layout>
    </Guard>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* The homepage is public, and stays put for a signed-in
              visitor rather than bouncing them to their dashboard - the
              bar offers them their courses instead. */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/cyberpath" element={<CyberPath />} />
          <Route path="/login" element={<Login />} />
          {/* Public self-signup removed — old links land on sign-in. */}
          <Route path="/register" element={<Navigate to="/login" replace />} />

          {/* ---------- students ---------- */}
          <Route
            path="/learn"
            element={<Shell roles={['student']} playful><CourseSelect /></Shell>}
          />
          <Route
            path="/learn/course/:trackSlug"
            element={<Shell roles={['student']} playful wide><CoursePage /></Shell>}
          />
          <Route
            path="/learn/badges"
            element={<Shell roles={['student']} playful><Badges /></Shell>}
          />
          <Route
            path="/learn/me"
            element={<Shell roles={['student']} playful><Profile /></Shell>}
          />
          <Route
            path="/learn/lesson/:slug"
            element={<Shell roles={['student', 'admin', 'superadmin']} playful><LessonPlayer /></Shell>}
          />

          {/* ---------- school reporting (admin + superadmin) ---------- */}
          <Route
            path="/admin"
            element={
              <Shell roles={['admin', 'superadmin']} wide>
                <SchoolScopeProvider><AdminHome /></SchoolScopeProvider>
              </Shell>
            }
          />
          <Route
            path="/admin/students"
            element={
              <Shell roles={['admin', 'superadmin']} wide>
                <SchoolScopeProvider><AdminStudents /></SchoolScopeProvider>
              </Shell>
            }
          />
          <Route
            path="/admin/students/:id"
            element={
              <Shell roles={['admin', 'superadmin']} wide>
                <SchoolScopeProvider><AdminStudentDetail /></SchoolScopeProvider>
              </Shell>
            }
          />

          {/* ---------- superadmin ---------- */}
          <Route path="/manage" element={<Shell roles={['superadmin']} wide><SuperHome /></Shell>} />
          <Route path="/manage/schools" element={<Shell roles={['superadmin']} wide><SuperSchools /></Shell>} />
          <Route path="/manage/users" element={<Shell roles={['superadmin']} wide><SuperUsers /></Shell>} />
          <Route path="/manage/payments" element={<Shell roles={['superadmin']} wide><SuperPayments /></Shell>} />
          <Route path="/manage/messages" element={<Shell roles={['superadmin']} wide><SuperMessages /></Shell>} />
          <Route path="/manage/audit" element={<Shell roles={['superadmin']} wide><SuperAudit /></Shell>} />

          <Route
            path="/checkout"
            element={<Shell roles={['student']} playful><Checkout /></Shell>}
          />

          {/* ---------- everyone ---------- */}
          {/* Practical sites open in their own tab, so they get no Shell
              chrome - the lesson stays open in the tab behind. */}
          <Route
            path="/lab/pc-components"
            element={<Shell roles={['student', 'superadmin']} wide><PcComponentsLab /></Shell>}
          />

          <Route
            path="/lab/program-launch"
            element={<Shell roles={['student', 'superadmin']} wide><ProgramLaunchLab /></Shell>}
          />

          <Route
            path="/lab/boot-loader"
            element={<Shell roles={['student', 'superadmin']} wide><BootLoaderLab /></Shell>}
          />

          <Route
            path="/lab/os-types"
            element={<Shell roles={['student', 'superadmin']} wide><OsTypesLab /></Shell>}
          />

          <Route
            path="/lab/windows-basics"
            element={<Shell roles={['student', 'superadmin']} wide><WindowsBasicsLab /></Shell>}
          />

          <Route
            path="/lab/windows-cli"
            element={<Shell roles={['student', 'superadmin']} wide><WindowsCliLab /></Shell>}
          />

          <Route
            path="/lab/linux-basics"
            element={<Shell roles={['student', 'superadmin']} wide><LinuxBasicsLab /></Shell>}
          />

          <Route
            path="/lab/linux-cli"
            element={<Shell roles={['student', 'superadmin']} wide><LinuxCliLab /></Shell>}
          />

          <Route
            path="/lab/macos-basics"
            element={<Shell roles={['student', 'superadmin']} wide><MacBasicsLab /></Shell>}
          />

          <Route
            path="/lab/macos-cli"
            element={<Shell roles={['student', 'superadmin']} wide><MacCliLab /></Shell>}
          />

          <Route
            path="/lab/trade-offs"
            element={<Shell roles={['student', 'superadmin']} wide><TradeOffsLab /></Shell>}
          />

          <Route
            path="/lab/scope"
            element={<Shell roles={['student', 'superadmin']} wide><ScopeLab /></Shell>}
          />

          <Route
            path="/lab/recon"
            element={<Shell roles={['student', 'superadmin']} wide><ReconLab /></Shell>}
          />

          <Route
            path="/lab/inbox"
            element={<Shell roles={['student', 'superadmin']} wide><InboxLab /></Shell>}
          />

          <Route
            path="/lab/harden"
            element={<Shell roles={['student', 'superadmin']} wide><HardenLab /></Shell>}
          />

          {/* The web-hacking track (Modules 3-14) shares one config-driven
              lab; :id selects the scenario. */}
          <Route
            path="/lab/web/:id"
            element={<Shell roles={['student', 'superadmin']} wide><WebLab /></Shell>}
          />
















          <Route
            path="/watch/junior-internet"
            element={<Shell roles={['student', 'superadmin']} wide><JuniorInternet /></Shell>}
          />

          <Route
            path="/lab/message-journey"
            element={<Shell roles={['student', 'superadmin']} wide><MessageJourneyLab /></Shell>}
          />

          <Route
            path="/lab/trap-spotter"
            element={<Shell roles={['student', 'superadmin']} wide><TrapSpotterLab /></Shell>}
          />

          <Route path="/account" element={<Shell><Account /></Shell>} />

          <Route
            path="*"
            element={
              <Shell>
                <Empty
                  icon="🧭"
                  title="This page went exploring"
                  text="We could not find that page. Use the menu at the top to get back on track."
                />
              </Shell>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
